import { useEffect, useState } from 'react';
import { kml as kmlToGeoJson } from '@tmcw/togeojson';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { collectIconTintRequests, lookAtRangeOf, parseKml, PointCandidate, selectPreviewPointIds } from './kmlHelpers';
import { extractKmlText } from './kmlFile';
import { applyKmlStyles } from './kmlStyles';
import { infoWindowOptionsOf } from './kmlInfoWindow';
import mapStyles from '../Compounds/MapWidget/mapStyles.json';
import { DocumentPreview } from './DocumentPreviewTool';
import { downloadAttachment } from '../../Api/Attachments/attachmentsApi';
import { downloadS3AttachmentApi } from '../../Api/Admin/S3Attachments/s3AttachmentApi';
import { fetchTintedKmlIcon } from '../../Api/Kml/kmlApi';
import { ComponentSpinner } from '../Compounds/Loading/ComponentSpinner';
import { TextBlock } from './Text';
import { getConfiguration } from '../../store/Configuration';
import { withGoogleMapsApiKey } from '../../utils/withGoogleMapsApiKey';

const defaultCenter = {
  lat: 42,
  lng: -100
};

export interface MapDocPreviewerProps {
  file: DocumentPreview;
  isPreview?: boolean;
}

const MapDocPreviewerContent = ({ file, isPreview = false }: MapDocPreviewerProps) => {
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [kmlBlob, setKmlBlob] = useState<Blob | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [errorState, setErrorState] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: getConfiguration().REACT_APP_GOOGLE_MAPS_API_KEY
  });

  const handleMapLoad = (map: google.maps.Map) => {
    setMapInstance(map);
  };

  const renderErrorElement = () => (
    <>
      <TextBlock
        size="xs"
        className="cursor-default">
        Error rendering {file.fileName}
      </TextBlock>
      <TextBlock
        size="xs"
        weight="thin"
        className="cursor-default">
        Unable to preview this map file
      </TextBlock>
    </>
  );

  // Download the KML file through the API (server proxies to S3, avoiding CORS).
  useEffect(() => {
    let cancelled = false;

    const downloadKml = async () => {
      setErrorState(false);
      setFileLoading(true);

      const { data, error } = file.isS3 ? await downloadS3AttachmentApi(file.id) : await downloadAttachment(file.id);

      if (cancelled) return;

      if (error || !data) {
        setErrorState(true);
      } else {
        setKmlBlob(data);
      }
      setFileLoading(false);
    };

    downloadKml();

    return () => {
      cancelled = true;
    };
  }, [file.id]);

  // Once both the map instance and the blob are ready, parse and render the KML.
  useEffect(() => {
    if (!mapInstance || !kmlBlob) return;

    let cancelled = false;
    const listeners: google.maps.MapsEventListener[] = [];
    let infoWindow: google.maps.InfoWindow | null = null;
    const blobUrls: string[] = [];

    const renderKml = async () => {
      setIsRendering(true);
      try {
        const { kmlText, embeddedFiles } = await extractKmlText(kmlBlob, file.fileName);
        const geoJson = parseKml(kmlText, embeddedFiles);

        if (cancelled || !mapInstance) return;

        if (!geoJson.features.length) {
          setErrorState(true);
          return;
        }

        setErrorState(false);

        // Build a tint map: fetch a server-tinted Blob for every unique (icon, colour,
        // opacity) combination, reproducing KmlLayer’s server-side ABGR colour application.
        const tintMap = new Map<string, string>();
        const tintRequests = collectIconTintRequests(geoJson);

        if (tintRequests.length > 0) {
          await Promise.all(
            tintRequests.map(async ({ key, iconUrl, hexColor, opacity }) => {
              const { data } = await fetchTintedKmlIcon(iconUrl, hexColor, opacity);
              if (!data) return;
              const blobUrl = URL.createObjectURL(data);
              if (cancelled) {
                URL.revokeObjectURL(blobUrl);
                return;
              }
              blobUrls.push(blobUrl);
              tintMap.set(key, blobUrl);
            })
          );
        }

        if (cancelled || !mapInstance) return;

        // Clear any previously loaded features before adding new ones
        mapInstance.data.forEach((feature) => mapInstance.data.remove(feature));

        // The Data layer deduplicates by GeoJSON feature `id`. Features without an id,
        // or sharing the same id (e.g. copy-pasted Placemarks with the same XML id attribute),
        // silently overwrite each other. Always stamp a unique index-based id to guarantee
        // every feature renders regardless of what the KML originally declared.
        const featuresWithIds = {
          ...geoJson,
          features: geoJson.features.map((f: ReturnType<typeof kmlToGeoJson>['features'][number], i: number) => ({
            ...f,
            id: i
          }))
        };
        mapInstance.data.addGeoJson(featuresWithIds);

        // Limit the preview to the easternmost and westernmost Point features to avoid
        // cluttering the small preview window with dense overlapping icons.
        // Only applied in isPreview mode — the full popup modal shows all icons.
        // Prefer features with no LookAt range constraint (always visible) over those
        // that would be hidden at the overview zoom level. Only fall back to constrained
        // features when no unconstrained ones exist.
        // Non-Point features (lines, polygons) are always shown regardless.
        let pointFilterIds: ReadonlySet<number> | undefined;

        if (isPreview) {
          const allPoints: PointCandidate[] = [];

          mapInstance.data.forEach((feature) => {
            const geom = feature.getGeometry();
            if (geom?.getType() !== 'Point') return;
            const id = feature.getId() as number;
            const lng = (geom as google.maps.Data.Point).get().lng();
            const lookAtRange = lookAtRangeOf(feature);
            allPoints.push({ id, lng, constrained: !isNaN(lookAtRange) && lookAtRange <= 800 });
          });

          pointFilterIds = selectPreviewPointIds(allPoints);
        }

        // Pass a 0.6 scale multiplier in preview mode to keep icons from spilling
        // outside the small preview container.
        listeners.push({ remove: applyKmlStyles(mapInstance, tintMap, pointFilterIds, isPreview ? 0.6 : 1) });

        // Open an InfoWindow on feature click, showing the KML name and popup body.
        infoWindow = new window.google.maps.InfoWindow();
        listeners.push(
          mapInstance.data.addListener('click', (event: google.maps.Data.MouseEvent) => {
            const options = infoWindowOptionsOf((key) => event.feature.getProperty(key));
            if (!options) return;

            infoWindow!.setOptions(options);
            infoWindow!.setPosition(event.latLng);
            infoWindow!.open(mapInstance);
          })
        );

        // Fit viewport to encompass all loaded features
        const bounds = new window.google.maps.LatLngBounds();
        mapInstance.data.forEach((feature) => {
          feature.getGeometry()?.forEachLatLng((latlng) => bounds.extend(latlng));
        });
        if (!bounds.isEmpty()) {
          window.requestAnimationFrame(() => {
            if (!cancelled) mapInstance.fitBounds(bounds, 40);
          });
        }
      } catch (err) {
        console.error('[MapDocPreviewer] Failed to render KML/KMZ:', err);
        if (!cancelled) setErrorState(true);
      } finally {
        if (!cancelled) setIsRendering(false);
      }
    };

    renderKml();

    return () => {
      cancelled = true;
      listeners.forEach((l) => l.remove());
      infoWindow?.close();
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [mapInstance, kmlBlob]);

  if (!isLoaded)
    return (
      <div
        className="p-2 h-[96%] w-[100%]"
        data-testid="map-doc-previewer">
        <TextBlock
          size="xs"
          weight="thin"
          className="cursor-default">
          Map previewer unavailable
        </TextBlock>
      </div>
    );

  return (
    <div
      className="p-2 h-[96%] w-[100%]"
      data-testid="map-doc-previewer">
      {(fileLoading || isRendering) && <ComponentSpinner />}
      {errorState && !fileLoading && !isRendering && renderErrorElement()}
      <div className={`h-full w-full ${fileLoading || isRendering || errorState ? 'invisible' : 'visible'}`}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          options={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            styles: mapStyles,
            zoomControl: false
          }}
          center={defaultCenter}
          onLoad={handleMapLoad}
          mapContainerClassName="rounded-lg"
          zoom={3}
        />
      </div>
    </div>
  );
};

export const MapDocPreviewer = withGoogleMapsApiKey(MapDocPreviewerContent);
